import { useState, useEffect, useCallback } from "react";
import { isWorkHubPath, workHubPageFor } from "@/pages/work-hub/workHub";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useBusiness } from "@/contexts/BusinessContext";
import BusinessSelectionModal from "@/components/BusinessSelectionModal";
import { Clock, LogOut, ShieldAlert, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map routes to page permission keys
const ROUTE_TO_PAGE = {
  "/dashboard": "dashboard",
  "/leads": "leads",
  "/contacts": "contacts",
  "/calendar": "calendar",
  "/tasks": "tasks",
  "/automation": "automation",
  "/reports": "reports",
  "/settings": "settings",
  "/work-planner": "work_planner",
  "/work-hub": "work_hub",
  "/work-hub/clients": "clients",
  "/work-hub/tasks": "tasks",
  "/work-hub/planner": "work_planner",
  "/work-hub/calendar": "work_calendar",
  "/work-hub/team-workload": "team_workload",
  "/work-hub/reports": "work_reports",
  "/whatsapp": "whatsapp",
  "/profile": "profile" // Profile is always accessible
};

// The page a restricted user (manager/employee/etc.) should land on: their first
// granted page, else their profile. Prevents dead-ending on /dashboard when the
// user has some access but not to the default page.
const homeRouteFor = (user) => {
  if (!user) return "/dashboard";
  if (user.role === "super_admin" || user.role === "admin") return "/dashboard";
  const perms = user.effective_permissions || [];
  if (perms.includes("dashboard")) return "/dashboard";
  const entry = Object.entries(ROUTE_TO_PAGE).find(([, page]) => page !== "profile" && perms.includes(page));
  return entry ? entry[0] : "/profile";
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );
  const [user, setUser] = useState(location.state?.user || null);
  const [isPending, setIsPending] = useState(false);
  const [pendingMessage, setPendingMessage] = useState("");
  const [showBusinessSelection, setShowBusinessSelection] = useState(false);
  const [hasPageAccess, setHasPageAccess] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { selectedBusiness, isLoading: businessLoading, clearBusiness } = useBusiness();

  // Check auth status on mount and when refreshing
  useEffect(() => {
    // If we have a user from navigation state and they're not pending, use it
    if (location.state?.user && !location.state.user.is_pending) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      setIsPending(false);
      return;
    }

    // Otherwise check with the server using XMLHttpRequest for better iOS compatibility
    const checkAuth = () => {
      setIsRefreshing(true);
      
      // Try XMLHttpRequest first (better cross-browser support)
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${BACKEND_URL}/api/auth/me`, true);
        xhr.withCredentials = true;
        xhr.setRequestHeader('Accept', 'application/json');
        
        xhr.onreadystatechange = function() {
          if (xhr.readyState === 4) {
            setIsRefreshing(false);
            
            // Handle 403 - need to check if it's pending
            if (xhr.status === 403) {
              try {
                const data = JSON.parse(xhr.responseText);
                if (data.detail && data.detail.toLowerCase().includes("pending")) {
                  setIsPending(true);
                  setPendingMessage(data.detail);
                  setIsAuthenticated(true);
                  return;
                }
              } catch (e) {
                // Not JSON
              }
              setIsAuthenticated(false);
              return;
            }
            
            if (xhr.status !== 200) {
              // Try localStorage fallback for iOS Safari
              const storedUser = localStorage.getItem("user_info");
              if (storedUser) {
                try {
                  const parsed = JSON.parse(storedUser);
                  if (parsed.logged_in) {
                    console.log("Using localStorage fallback for auth");
                    // We have localStorage data but cookie failed
                    // This might be iOS Safari blocking cookies
                    // Clear localStorage and force re-login
                    localStorage.removeItem("user_info");
                  }
                } catch (e) {}
              }
              setIsAuthenticated(false);
              return;
            }
            
            try {
              const userData = JSON.parse(xhr.responseText);
              setIsAuthenticated(true);
              setUser(userData);
              
              // Update localStorage with fresh data
              localStorage.setItem("user_info", JSON.stringify({
                user_id: userData.user_id,
                name: userData.name,
                role: userData.role,
                email: userData.email,
                logged_in: true
              }));
              
              if (userData.is_pending || userData.status === "pending") {
                setIsPending(true);
                setPendingMessage(userData.status_message || "Your account is pending admin approval.");
              } else {
                setIsPending(false);
                setPendingMessage("");
              }
            } catch (error) {
              console.error("Auth check error:", error);
              setIsAuthenticated(false);
            }
          }
        };
        
        xhr.onerror = function() {
          setIsRefreshing(false);
          console.error("Auth check network error");
          setIsAuthenticated(false);
        };
        
        xhr.ontimeout = function() {
          setIsRefreshing(false);
          console.error("Auth check timeout");
          setIsAuthenticated(false);
        };
        
        xhr.timeout = 10000; // 10 second timeout
        xhr.send();
      } catch (error) {
        console.error("Auth check exception:", error);
        setIsRefreshing(false);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []); // Only run once on mount

  // Manual refresh function for pending users
  const refreshAuthStatus = () => {
    setIsRefreshing(true);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${BACKEND_URL}/api/auth/me`, true);
    xhr.withCredentials = true;
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        setIsRefreshing(false);
        
        if (xhr.status === 403) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data.detail && data.detail.toLowerCase().includes("pending")) {
              setIsPending(true);
              setPendingMessage(data.detail);
              return;
            }
          } catch (e) {}
          setIsAuthenticated(false);
          setIsPending(false);
          return;
        }
        
        if (xhr.status !== 200) {
          setIsAuthenticated(false);
          setIsPending(false);
          return;
        }
        
        try {
          const userData = JSON.parse(xhr.responseText);
          setIsAuthenticated(true);
          setUser(userData);
          
          if (userData.is_pending || userData.status === "pending") {
            setIsPending(true);
            setPendingMessage(userData.status_message || "Your account is pending admin approval.");
          } else {
            // User is now approved!
            setIsPending(false);
            setPendingMessage("");
          }
        } catch (error) {
          console.error("Auth refresh error:", error);
        }
      }
    };
    
    xhr.onerror = function() {
      setIsRefreshing(false);
      console.error("Auth refresh network error");
    };
    
    xhr.send();
  };

  // Check page-level permissions
  useEffect(() => {
    if (user && !isPending) {
      const currentPath = location.pathname;
      // Work Planner sub-pages (/work-planner/performance, /work-planner/client/:id …) share
      // the parent's permission instead of falling through ungated.
      // Work Hub sub-pages (/work-hub/clients/:id, /work-planner/client/:id …) inherit their section's permission.
      const pageKey = ROUTE_TO_PAGE[currentPath] ?? (isWorkHubPath(currentPath) ? workHubPageFor(currentPath) : undefined);
      
      // Profile is always accessible
      if (pageKey === "profile") {
        setHasPageAccess(true);
        return;
      }
      
      // Super Admin and Admin always have full access
      if (user.role === "super_admin" || user.role === "admin") {
        setHasPageAccess(true);
        return;
      }
      
      // Check effective permissions
      const effectivePermissions = user.effective_permissions || [];
      if (pageKey && !effectivePermissions.includes(pageKey)) {
        setHasPageAccess(false);
      } else {
        setHasPageAccess(true);
      }
    }
  }, [user, location.pathname, isPending]);

  // Check if business selection is needed after authentication
  useEffect(() => {
    if (isAuthenticated && user && !businessLoading && !isPending) {
      // Super Admin can proceed without business selection (they have the switcher)
      // For all other roles, auto-assign their business if they have one
      if (user.role === "super_admin" && !selectedBusiness) {
        // Super Admin without selection - show selector
        setShowBusinessSelection(true);
      } else if (user.role !== "super_admin" && !selectedBusiness && user.business_id) {
        // Non-super-admin with assigned business - auto-select their business
        // This is handled by BusinessSelectionModal
        setShowBusinessSelection(true);
      } else if (user.role !== "super_admin" && !selectedBusiness && !user.business_id) {
        // Non-super-admin without business assignment - show no business assigned message
        setShowBusinessSelection(true);
      } else {
        setShowBusinessSelection(false);
      }
    }
  }, [isAuthenticated, user, selectedBusiness, businessLoading, isPending]);

  const handleBusinessSelected = (business) => {
    setShowBusinessSelection(false);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      clearBusiness();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login");
    }
  };

  if (isAuthenticated === null || businessLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-slate-50 to-pink-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        <div className="animate-pulse text-indigo-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show pending approval screen
  if (isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-indigo-50/30 to-pink-50/30 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-amber-600 dark:text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
            Account Pending Approval
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            {pendingMessage || "Your account is pending admin approval. Please wait for an admin to approve your account."}
          </p>
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              You will be able to access the CRM once an administrator approves your account.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              onClick={refreshAuthStatus}
              disabled={isRefreshing}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="refresh-status-button"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? "Checking..." : "Check Status"}
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show access denied screen if user doesn't have permission for this page
  if (!hasPageAccess) {
    // If the user CAN access another page, send them there instead of dead-ending
    // (e.g. a manager granted only Client Work Planner shouldn't be stuck on the
    // denied Dashboard they were redirected to at login).
    const homeRoute = homeRouteFor(user);
    if (homeRoute !== "/profile" && homeRoute !== location.pathname) {
      return <Navigate to={homeRoute} replace />;
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-indigo-50/30 to-pink-50/30 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldAlert className="w-10 h-10 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
            Access Denied
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            You don't have permission to access this page. Please contact your administrator if you believe this is an error.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate(homeRouteFor(user))}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Go to my pages
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show business selection modal if needed
  if (showBusinessSelection) {
    return (
      <BusinessSelectionModal 
        onComplete={handleBusinessSelected} 
        user={user}
      />
    );
  }

  return children;
};

export default ProtectedRoute;
